import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchProductByHandle, formatPrice } from "@/lib/shopify";
import { useCartStore } from "@/lib/cartStore";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Loader2, Sparkles } from "lucide-react";
import aureliaModel from "@/assets/models/aurelia-model.jpg";
import biancaModel from "@/assets/models/bianca-model.jpg";
import camilleModel from "@/assets/models/camille-model.jpg";
import eliseModel from "@/assets/models/elise-model.jpg";
import isabelleModel from "@/assets/models/isabelle-model.jpg";
import margotModel from "@/assets/models/margot-model.jpg";
import siennaModel from "@/assets/models/sienna-model.jpg";
import vivienneModel from "@/assets/models/vivienne-model.jpg";

const MODEL_SHOTS: Record<string, string> = {
  "the-aurelia-layered-honey-blonde-bob": aureliaModel,
  "the-bianca-layered-lace-top-bob": biancaModel,
  "the-camille-chestnut-silk-top-bob": camilleModel,
  "the-elise-darkest-brown-silk-top-12": eliseModel,
  "the-isabelle-wavy-layered-silk-top-14": isabelleModel,
  "the-margot-golden-brown-lace-top-15": margotModel,
  "the-sienna-layered-silk-top-15": siennaModel,
  "the-vivienne-natural-curly-silk-top-12": vivienneModel,
};

export const Route = createFileRoute("/product/$handle")({
  loader: async ({ params, context }) => {
    const product = await context.queryClient.ensureQueryData({
      queryKey: ["product", params.handle],
      queryFn: () => fetchProductByHandle(params.handle),
    });
    return { product: product as FullProduct | null };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.product;
    const title = p ? `${p.title} — European Wig Luxury` : `${params.handle.replace(/-/g, " ")} — European Wig Luxury`;
    const description = p?.description
      ? p.description.slice(0, 160)
      : "Hand-tied, 100% virgin European hair wig — unprocessed and cuticle-aligned.";
    const image = p?.images.edges[0]?.node.url;
    const url = `https://europeanwigluxury.com/product/${params.handle}`;
    const meta = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    const scripts: Array<{ type: string; children: string }> = [];
    if (p) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.title,
          description: p.description,
          image: p.images.edges.map((e) => e.node.url),
          brand: { "@type": "Brand", name: p.vendor || "European Wig Luxury" },
          offers: {
            "@type": "Offer",
            price: p.priceRange.minVariantPrice.amount,
            priceCurrency: p.priceRange.minVariantPrice.currencyCode,
            availability: p.variants.edges.some((v) => v.node.availableForSale)
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            url,
          },
        }),
      });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: ProductPage,
});

interface FullProduct {
  id: string;
  title: string;
  description: string;
  descriptionHtml: string;
  handle: string;
  tags: string[];
  vendor: string;
  productType: string;
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  images: { edges: Array<{ node: { url: string; altText: string | null } }> };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        price: { amount: string; currencyCode: string };
        availableForSale: boolean;
        selectedOptions: Array<{ name: string; value: string }>;
      };
    }>;
  };
  options: Array<{ name: string; values: string[] }>;
}

function tagsBy(tags: string[], prefix: string) {
  return tags.filter((t) => t.toLowerCase().startsWith(prefix + ":")).map((t) => t.split(":").slice(1).join(":").trim());
}

function ProductPage() {
  const { handle } = Route.useParams();
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const [imgIdx, setImgIdx] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [variantIdx, setVariantIdx] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setImgIdx(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const { data: product, isPending, isError } = useQuery({
    queryKey: ["product", handle],
    queryFn: async () => {
      const p = await fetchProductByHandle(handle);
      if (!p) throw notFound();
      return p as FullProduct;
    },
  });

  if (isPending) {
    return <div className="container-luxe flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (isError || !product) {
    return (
      <div className="container-luxe py-24 text-center">
        <h1 className="font-display text-3xl">Piece not found</h1>
        <p className="mt-2 text-muted-foreground">This wig may have sold out or been renamed.</p>
        <Button asChild className="mt-6"><Link to="/shop">Back to shop</Link></Button>
      </div>
    );
  }

  const productImages = product.images.edges;
  const modelShot = MODEL_SHOTS[product.handle];
  const images = modelShot
    ? [{ node: { url: modelShot, altText: `Model wearing ${product.title}` } }, ...productImages]
    : productImages;
  const variants = product.variants.edges;
  const variant = variants[variantIdx]?.node ?? variants[0]?.node;
  const price = variant?.price ?? product.priceRange.minVariantPrice;

  const cap = tagsBy(product.tags, "cap");
  const texture = tagsBy(product.tags, "texture");
  const color = tagsBy(product.tags, "color");
  const density = tagsBy(product.tags, "density");

  const handleAdd = async () => {
    if (!variant) return;
    await addItem({
      product: { node: {
        id: product.id, title: product.title, description: product.description, handle: product.handle,
        tags: product.tags, productType: product.productType, vendor: product.vendor,
        priceRange: product.priceRange, images: product.images,
        variants: product.variants, options: product.options,
      } },
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions ?? [],
    });
  };

  return (
    <section className="container-luxe py-10 md:py-16">
      <nav className="mb-6 text-xs text-muted-foreground">
        <Link to="/shop" className="hover:text-foreground">Shop</Link> / <span>{product.title}</span>
      </nav>
      <div className="grid gap-12 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <Carousel setApi={setCarouselApi} opts={{ loop: false }} className="w-full">
            <CarouselContent>
              {images.map((im, i) => (
                <CarouselItem key={i}>
                  <div className="aspect-[4/5] overflow-hidden rounded-md bg-muted">
                    <img
                      src={im.node.url}
                      alt={im.node.altText ?? product.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {images.length > 1 && (
            <>
              <div className="mt-3 flex justify-center gap-1.5 md:hidden">
                {images.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to image ${i + 1}`}
                    onClick={() => carouselApi?.scrollTo(i)}
                    className={`h-1.5 rounded-full transition-all ${imgIdx === i ? "w-6 bg-bronze" : "w-1.5 bg-muted-foreground/40"}`}
                  />
                ))}
              </div>
              <div className="mt-4 hidden grid-cols-5 gap-2 md:grid">
                {images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => carouselApi?.scrollTo(i)}
                    aria-label={`View product image ${i + 1}`}
                    aria-pressed={imgIdx === i}
                    className={`aspect-square overflow-hidden rounded ${imgIdx === i ? "ring-2 ring-bronze" : "opacity-70 hover:opacity-100"}`}
                  >
                    <img src={im.node.url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>


        {/* Details */}
        <div>
          <p className="eyebrow">Short length · Virgin European hair</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl">{product.title}</h1>
          <p className="mt-4 font-display text-2xl">{formatPrice(price.amount, price.currencyCode)}</p>
          {product.description && (
            <p className="mt-4 max-w-lg text-muted-foreground">{product.description}</p>
          )}

          {product.options?.length > 0 && product.options[0].name.toLowerCase() !== "title" && (
            <div className="mt-8 space-y-4">
              {product.options.map((opt) => (
                <div key={opt.name}>
                  <p className="eyebrow mb-2">{opt.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {variants.map((v, i) => {
                      const val = v.node.selectedOptions.find((o) => o.name === opt.name)?.value;
                      if (!val) return null;
                      const active = variantIdx === i;
                      return (
                        <button
                          key={v.node.id}
                          onClick={() => setVariantIdx(i)}
                          className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${active ? "border-bronze bg-bronze text-primary-foreground" : "border-border hover:border-foreground"}`}
                        >{val}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" className="h-12 flex-1 rounded-full" onClick={handleAdd} disabled={isLoading || !variant?.availableForSale}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : variant?.availableForSale ? "Add to bag" : "Sold out"}
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 rounded-full">
              <Link to="/contact"><Sparkles className="mr-2 h-4 w-4" />Contact us</Link>
            </Button>
          </div>

          <p className="mt-5 rounded border border-border/70 bg-secondary/50 p-4 text-sm">
            Need this for medical hair loss? We provide a{" "}
            <Link to="/faq" hash="insurance" className="font-medium underline">cranial prosthesis invoice</Link>{" "}
            for insurance claims — ask at checkout.
          </p>

          <div className="mt-10">
            <Accordion type="single" collapsible defaultValue="why">
              <AccordionItem value="why">
                <AccordionTrigger>Why this is {formatPrice(price.amount, price.currencyCode)}</AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm text-muted-foreground">
                  <p><strong className="text-foreground">100% virgin European hair.</strong> Unprocessed hair sourced for its fine-to-medium density and natural blonde-to-brunette range — never dyed, bleached, permed, or acid-bathed.</p>
                  <p><strong className="text-foreground">Cuticle-aligned & unprocessed.</strong> Never dyed, bleached, or acid-bathed. Virgin hair keeps its natural strength, shine, and lifespan.</p>
                  <p><strong className="text-foreground">Hand-tied cap.</strong> Each strand is individually knotted into the cap by hand — no wefts. That's what creates the natural movement, invisible part, and breathable feel.</p>
                  {cap.length > 0 && <p><strong className="text-foreground">Cap construction.</strong> {cap.join(", ")} — chosen for a natural hairline and breathability.</p>}
                  <p><strong className="text-foreground">Expected lifespan.</strong> Cared for properly, virgin European hair pieces routinely last 3–5+ years. Lower-cost, chemically processed pieces typically break down in 6–12 months as the processing wears off.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="specs">
                <AccordionTrigger>Specifications</AccordionTrigger>
                <AccordionContent>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <SpecRow label="Hair origin" value="Virgin European" />
                    <SpecRow label="Processing" value="100% virgin — no dye, bleach, or perm" />
                    <SpecRow label="Cuticle" value="Aligned root-to-tip" />
                    <SpecRow label="Construction" value="Hand-tied" />
                    {cap.length > 0 && <SpecRow label="Cap type" value={cap.join(", ")} />}
                    {texture.length > 0 && <SpecRow label="Texture" value={texture.join(", ")} />}
                    {color.length > 0 && <SpecRow label="Color" value={color.join(", ")} />}
                    {density.length > 0 && <SpecRow label="Density" value={density.join(", ")} />}
                    <SpecRow label="Length" value="Short" />
                  </dl>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="sizing">
                <AccordionTrigger>Sizing & fit</AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>This piece is a <strong className="text-foreground">Medium</strong> cap (22–22.5" / 56–57 cm circumference) with an adjustable strap for a refined fit.</p>
                  <p>Not sure of your size? See our <Link to="/size-guide" className="underline">wig cap size guide</Link>, or <Link to="/contact" className="underline">contact us</Link> for a custom fit.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care">
                <AccordionTrigger>Care</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  Wash every 8–15 wears using a sulfate-free shampoo, cool water, and a wide-tooth comb. Air-dry on a wig stand.
                  For full instructions see our <Link to="/care" className="underline">care guide</Link>.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </>
  );
}
